// ---- Fish-shadow mirror (Living Waters) ---------------------------------------
// The client render half of the server's water-population fish — the 4th
// interest-managed entity kind. EVERY nearby client renders these (not just the
// caster — that's why this isn't folded into fishing.ts, the caster's activity
// module): a flattened dark lozenge just under the water surface, sized by the
// species' `fish.size`, interpolated exactly like networked NPCs
// (net.ts reconcileNpcs/updateNetNpcs), with cheap transform-only FX driven by
// the wire behavior state `st` (swim wiggle / dart stretch / nibble jitter /
// hooked thrash).
//
// Materials: ONE shared MeshBasicMaterial (deliberately not toon — a shadow
// shouldn't cel-shade) + ONE shared geometry, added at the SCENE root like net
// floats, so disposeWorld/map swaps never sweep them; resetFish() is the
// explicit teardown (net.ts resetMirrors + game.ts swapWorld call it).
import * as THREE from "three";
import { WATER_LEVEL } from "./terrain.ts";
import { itemSpecOrNull } from "./items/index.ts";
import { FISHING_TUNING as FT } from "./tunables.ts";
import { sampleBuffer, type InterpSnap, type InterpOut } from "./interp.ts";
import type { NetFish, FishWireState } from "./shared.ts";
import { hideOutdoorWhileIndoor } from "./cutaway.ts";

let scene: THREE.Scene | null = null;

const GEO = new THREE.CircleGeometry(0.5, 12).rotateX(-Math.PI / 2);
// The shadow sits a hair UNDER the water surface. The water plane no longer
// writes depth (buildWater in terrain.ts), so it doesn't occlude the shadow —
// but the shadow DOES depth-test (default), so SOLID geometry above it (piers,
// banks, lily pads) hides it correctly. renderOrder 2 draws it over the water in
// the transparent pass; the bluish, low-opacity tone reads as a fish just below
// the surface rather than a hard decal on top of it.
const MAT = new THREE.MeshBasicMaterial({
  color: 0x16303d,
  transparent: true,
  opacity: 0.45,
  depthWrite: false,
});

type FishMirror = {
  mesh: THREE.Mesh;
  size: number;
  st: FishWireState;
  buf: InterpSnap[]; // shared interpolation buffer (surface-locked, so y is always 0)
  bx: number; bz: number; bry: number; // last interpolated BASE transform (FX added on top each frame)
  phase: number; // per-fish FX phase offset
};

const fishes = new Map<string, FishMirror>();
const FISH_INTERP: InterpOut = { x: 0, y: 0, z: 0, ry: 0 }; // reused scratch (consumed each iter)

export function initFish(s: THREE.Scene): void {
  scene = s;
}

/** Fold a `state` snapshot's fish records in (net.ts calls this): enter =
 *  create + snap, else buffer the transform for interpolation. */
export function reconcileFish(list: NetFish[], serverTs?: number): void {
  const hasTs = typeof serverTs === "number" && Number.isFinite(serverTs);
  const stamp = hasTs ? (serverTs as number) : performance.now();
  for (const nf of list) {
    let m = fishes.get(nf.id);
    if (!m) {
      const size = itemSpecOrNull(nf.type)?.fish?.size ?? 1;
      const mesh = new THREE.Mesh(GEO, MAT);
      mesh.scale.set(2.2 * size, 1, 0.9 * size); // an elongated shadow, nose = +z
      mesh.renderOrder = 2; // draw over the (depth-writing) water plane
      mesh.position.set(nf.x, WATER_LEVEL - FT.shadowDepthOffset, nf.z);
      mesh.rotation.y = nf.ry;
      scene?.add(mesh);
      m = { mesh, size, st: nf.st, buf: [], bx: nf.x, bz: nf.z, bry: nf.ry, phase: Math.random() * Math.PI * 2 };
      fishes.set(nf.id, m);
    }
    m.st = nf.st;
    m.buf.push({ t: stamp, x: nf.x, y: 0, z: nf.z, ry: nf.ry }); // y unused — shadow rides the surface
  }
}

/** Explicit removals (`leave.fish` — a landed fish vanishes for everyone). */
export function applyFishLeaves(ids: string[]): void {
  for (const id of ids) {
    const m = fishes.get(id);
    if (m) scene?.remove(m.mesh); // shared geo/mat — never disposed
    fishes.delete(id);
  }
}

/** Drop every mirrored fish (socket close / map swap — the mirrors re-enter). */
export function resetFish(): void {
  for (const m of fishes.values()) scene?.remove(m.mesh);
  fishes.clear();
}

/** Per-frame: shared delayed-bracket interpolation (`renderT` comes from net.ts's
 *  sample clock via sampleRenderT) + st-driven transform FX. */
export function updateFish(renderT: number, t: number): void {
  // Fish live outdoors only — hide every surface shadow while the local player
  // is indoors (exterior blackout). hideOutdoorWhileIndoor is true for any
  // water coord when sheltered (waters sit outside structure footprints).
  for (const m of fishes.values()) {
    // Interpolate the BASE transform; hold the last base when the buffer empties
    // (never the mesh position — that carries last frame's FX offset).
    if (sampleBuffer(m.buf, renderT, FISH_INTERP)) {
      m.bx = FISH_INTERP.x; m.bz = FISH_INTERP.z; m.bry = FISH_INTERP.ry;
    }
    const x = m.bx, z = m.bz, ry = m.bry;

    // Outdoor hide (player indoors): presentation-only; interest/wire unchanged.
    const hide = hideOutdoorWhileIndoor(x, z);
    if (m.mesh.visible === hide) m.mesh.visible = !hide;
    if (hide) continue;

    // st-driven cosmetic FX — transforms only (the material is shared).
    const p = m.phase;
    let sway = 0, jx = 0, jz = 0, stretch = 1, bob = 0;
    switch (m.st) {
      case "swim":
        sway = Math.sin(t * 2.4 + p) * 0.12;
        break;
      case "dart":
        stretch = 1.25;
        break;
      case "approach":
        sway = Math.sin(t * 4 + p) * 0.1;
        break;
      case "investigate":
        sway = Math.sin(t * 3 + p) * 0.2;
        bob = Math.sin(t * 1.6 + p) * 0.02;
        break;
      case "nibble":
        jx = Math.sin(t * 22 + p) * 0.05;
        jz = Math.cos(t * 19 + p) * 0.05;
        break;
      case "bite":
        jx = Math.sin(t * 30 + p) * 0.08;
        stretch = 1.1;
        break;
      case "hooked":
        sway = Math.sin(t * 16 + p) * 0.5;
        jx = Math.sin(t * 26 + p) * 0.1;
        jz = Math.cos(t * 23 + p) * 0.1;
        break;
    }
    m.mesh.position.set(x + jx, WATER_LEVEL - FT.shadowDepthOffset + bob, z + jz);
    m.mesh.rotation.y = ry + sway;
    m.mesh.scale.set(2.2 * m.size, 1, 0.9 * m.size * stretch);
  }
}
